import { DEFAULT_THEME, isThemeId, type EffectiveAppearance } from '@wodi/shared';

// Puts the theme and mode in effect on <html> (ADR 0030): data-theme picks the theme's
// colour tokens and data-mode="light" or "dark" picks the mode. "Match my device" is worked
// out here from prefers-color-scheme, and follows it live.
//
// The device keeps the last-used theme ID and mode in localStorage, and nothing else, so
// the inline script in index.html can apply them before first paint (no flash of the wrong
// theme) and the sign-in page looks the way the last person left it. Once the session
// loads, the server's value wins. Keep the keys and the logic in step with index.html.

export const THEME_KEY = 'wodi.theme';
export const MODE_KEY = 'wodi.mode';

const DARK_QUERY = '(prefers-color-scheme: dark)';

let current: EffectiveAppearance = { theme: DEFAULT_THEME, mode: 'system' };
let media: MediaQueryList | null = null;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Storage can be switched off; the theme then lasts for this page only.
    return null;
  }
}

/** The appearance last used on this device, or medical blue matching the device. */
export function storedAppearance(): EffectiveAppearance {
  const store = storage();
  const theme = store?.getItem(THEME_KEY) ?? null;
  const mode = store?.getItem(MODE_KEY) ?? null;
  return {
    theme: isThemeId(theme) ? theme : DEFAULT_THEME,
    mode: mode === 'light' || mode === 'dark' ? mode : 'system',
  };
}

function deviceIsDark(): boolean {
  return media?.matches ?? false;
}

/** The mode actually shown: "Match my device" becomes light or dark. */
export function resolvedMode(mode: EffectiveAppearance['mode']): 'light' | 'dark' {
  if (mode === 'system') return deviceIsDark() ? 'dark' : 'light';
  return mode;
}

function paint(): void {
  const root = document.documentElement;
  root.dataset['theme'] = current.theme;
  root.dataset['mode'] = resolvedMode(current.mode);
}

/** Applies an appearance now and remembers it on this device. */
export function applyAppearance(appearance: EffectiveAppearance): void {
  current = appearance;
  paint();
  const store = storage();
  try {
    store?.setItem(THEME_KEY, appearance.theme);
    store?.setItem(MODE_KEY, appearance.mode);
  } catch {
    // A full or blocked store only loses the no-flash start next time.
  }
}

/** The appearance applied now. */
export function currentAppearance(): EffectiveAppearance {
  return current;
}

/**
 * Starts from what this device used last (the inline script already painted it) and
 * follows the device's light or dark setting while "Match my device" is chosen.
 */
export function installAppearance(win: Window = window): void {
  // The waiting-area display (ADR 0013) is a shared screen read across a room: it stays
  // in the default theme, light, whoever used the device last.
  if (win.location.pathname.startsWith('/display')) {
    current = { theme: DEFAULT_THEME, mode: 'light' };
    paint();
    return;
  }
  media = typeof win.matchMedia === 'function' ? win.matchMedia(DARK_QUERY) : null;
  current = storedAppearance();
  paint();
  media?.addEventListener('change', () => {
    if (current.mode === 'system') paint();
  });
}
